import React, { FC, useCallback, useEffect, useState } from 'react';
import { Form, Input, Button, notification } from 'src/common';
import styles from './index.module.scss';
import formStyles from '../index.module.scss';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useCopyToClipboard } from 'react-use';
import { FormInstance } from 'antd/lib/form';
import { useSetBidQuestionAnswer, useTask } from 'src/models/bids/BidTask/hooks';
import { IAnswerFormState } from '../index';
import { faArrowsRepeat, faPen } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FeatureFlag, useFeatureFlag } from 'src/helpers/featureFlag';
import { trackBTGenerateAnswer, trackBTSavesFreeText } from 'src/segment/events';
import AddInstructionsModal from '../../AddInstructionsModal';
import { useSubscription } from '@apollo/client';
import { ON_TENJIN_ANSWER } from 'src/models/biddingGPT/queries';
import { useGenerateTenjinAnswer } from 'src/models/biddingGPT/hooks';
import { useParams } from 'react-router-dom';
import { TenjinStreamAnswer } from 'src/models/biddingGPT/types';
import { TenjinAnswerSub } from '@tendium/prom-types/subscriptions';

const { TextArea } = Input;

interface IProps {
  form: FormInstance<IAnswerFormState>;
  isFocused: boolean;
  setIsFocused: (isFocused: boolean) => void;
}

const fieldName = 'freeText';

export const FreeTextAnswer: FC<IProps> = props => {
  const { form, isFocused, setIsFocused } = props;
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);
  const { id: taskId } = useParams<{ id: string }>();

  const [, copyToClipboard] = useCopyToClipboard();
  const { data: task } = useTask();

  const [generateAnswer, { loading: loadingAnswer }] = useGenerateTenjinAnswer();
  const isBiddingGPTFeature = useFeatureFlag(FeatureFlag.BiddingTool_BiddingGPT);

  const [disableGenerate, setDisableGenerate] = useState<boolean>(false);
  const [fullMessage, setFullMessage] = useState('');

  const [streamStart, setStreamStart] = useState(false);

  useSubscription<TenjinStreamAnswer>(ON_TENJIN_ANSWER, {
    variables: { taskId },
    onData: ({ data }) => {
      if (!data) {
        return;
      }

      if (data.data?.tenjinAnswer.messageOrder === TenjinAnswerSub.Order.First) setStreamStart(true);
      const newChunk = data.data?.tenjinAnswer.content;
      setFullMessage(prevMessage => prevMessage + newChunk);
      if (data.data?.tenjinAnswer.messageOrder === TenjinAnswerSub.Order.Last) setDisableGenerate(false);
    }
  });

  const onCopy = useCallback(() => {
    const content = form.getFieldValue(fieldName);
    copyToClipboard(content);
    setIsCopied(true);
  }, [copyToClipboard, form]);

  const onValuesChange = useCallback(() => {
    setIsFocused(true);
  }, [setIsFocused]);
  const [setAnswer] = useSetBidQuestionAnswer();

  const onSave = useCallback(() => {
    if (!!task) {
      const content = form.getFieldValue(fieldName);

      setAnswer(
        {
          questionId: task.id,
          questionGroupId: task.groupId,
          answer: {
            content,
            answer: task.answer?.answer,
            files: task.answer?.files?.map(file => file.id)
          }
        },
        () => {
          setIsCopied(false);
          setIsFocused(false);
        }
      );
      trackBTSavesFreeText(task.id, task.groupId, task.bidId, task.type);
    }
  }, [task, form, setAnswer, setIsFocused]);

  const onCancel = useCallback(() => {
    form.resetFields([fieldName]);
    setIsFocused(false);
  }, [form, setIsFocused]);

  const onGenerate = useCallback(
    async (extraInstructions?: string) => {
      if (!!task && !!task.content) {
        trackBTGenerateAnswer(task.id, task.groupId, task.bidId, task.type);
        try {
          await generateAnswer({
            variables: {
              question: task.content,
              taskId: task.id,
              settings: { extraInstructions: extraInstructions ? [extraInstructions] : [] }
            }
          });
          setDisableGenerate(true);
          setFullMessage('');
          form.setFieldsValue({ freeText: '' });
          setIsFocused(true);
        } catch {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        }
      }
    },
    [form, generateAnswer, setIsFocused, t, task]
  );

  const [modalVisible, setModalVisible] = useState(false);

  const onOpenModal = useCallback(() => {
    setModalVisible(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setModalVisible(false);
  }, []);

  useEffect(() => {
    if (!streamStart) return;
    let displayedMessage = form.getFieldValue(fieldName) || '';
    if (fullMessage.length > displayedMessage.length) {
      let index = displayedMessage.length;
      const intervalId = setInterval(() => {
        if (index < fullMessage.length) {
          displayedMessage += fullMessage[index];
          form.setFieldsValue({ freeText: displayedMessage });
          index++;
        } else {
          clearInterval(intervalId);
        }
      }, 1);
      return () => {
        clearInterval(intervalId);
      };
    }
  }, [form, fullMessage, taskId, streamStart]);

  useEffect(() => {
    return () => {
      setDisableGenerate(false);
      setStreamStart(false);
    };
  }, [taskId]);

  return (
    <div className={formStyles.unit}>
      <div className={styles.inner}>
        <Form.Item name={fieldName} className={classNames(styles.textarea, { [styles.isFocused]: isFocused })}>
          <TextArea
            autoSize={{ minRows: 2 }}
            placeholder={t('BiddingTool.placeholderFreeText')}
            onFocus={onValuesChange}
          />
        </Form.Item>
        <Form.Item className={classNames(styles.buttonsContainer, { [styles.isFocused]: isFocused })}>
          <Button type={'text'} onClick={onCancel}>
            {t('Common.cancel')}
          </Button>
          <Button type={'primary'} onClick={onSave}>
            {t('Common.save')}
          </Button>
        </Form.Item>
      </div>
      <Form.Item className={classNames(styles.footer, { [styles.isBiddingGPT]: isBiddingGPTFeature })}>
        {isBiddingGPTFeature && (
          <div className={styles.generateAnswerActions}>
            <Button icon={<FontAwesomeIcon icon={faPen} />} onClick={onOpenModal}>
              {t('BiddingTool.addExtraInstructions')}
            </Button>
            <Button
              loading={loadingAnswer}
              type={'primary'}
              icon={<FontAwesomeIcon icon={faArrowsRepeat} />}
              onClick={() => onGenerate()}
              disabled={disableGenerate}
            >
              {t('BiddingTool.biddingGPTGenerateResponse')}
            </Button>
            {modalVisible && <AddInstructionsModal onClose={onCloseModal} onGenerate={onGenerate} />}
          </div>
        )}
        {form.getFieldValue(fieldName) && !isFocused && (
          <Button type={'default'} onClick={onCopy}>
            {isCopied ? t('Common.isCopied') : t('Common.copyText')}
          </Button>
        )}
      </Form.Item>
    </div>
  );
};

export default FreeTextAnswer;
